import { FormikHelpers } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { IEditableSectionProps } from "@/components/EditableSection";
import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { IAttachment } from "@/data/attachments";
import {
  createCompanyInsurance,
  fetchCompanyInsurance,
  ICompanyInsurance,
  putCompanyInsurance,
} from "@/data/companies";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";
import { bankingDetailsSchema } from "@/views/Settings/CompanySettings/BankingDetails/validationSchema";
import { CompanyInsurancePreview } from "@/views/Settings/CompanySettings/InsuranceDetails/CompanyInsurancePreview";
import {
  CompanyInsuranceSectionEdit,
  IInsuranceFormData,
} from "@/views/Settings/CompanySettings/InsuranceDetails/InsuranceAttachmentsEdit";

interface ICompanyInsuranceSectionProps {
  editableProps: IEditableSectionProps;
}

export const CompanyInsuranceSection = ({ editableProps }: ICompanyInsuranceSectionProps) => {
  const Company = useCompanyContext();

  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const { data: fetchedInsurance, refetch } = useQuery(
    `/api/companies/company-insurance/${Company.id}`,
    () => fetchCompanyInsurance(Company.id),
    {
      enabled: !!Company.id,
    },
  );

  const initialFileData = (file?: IAttachment): FileUploadStateItem<IAttachment> | undefined =>
    file?.id
      ? {
          attachment: file,
          file: undefined,
          error: undefined,
          progress: undefined,
        }
      : undefined;

  const initialValues = useMemo(
    () =>
      fetchedInsurance
        ? {
            ...fetchedInsurance,
            file: initialFileData(fetchedInsurance.file),
          }
        : {
            file: undefined,
            amount: 0,
            expirationDate: "",
            accepted: false,
            hasInsurance: false,
          },
    [fetchedInsurance],
  );

  const handleSubmit = async (values: IInsuranceFormData, actions: FormikHelpers<IInsuranceFormData>) => {
    const action = fetchedInsurance?.id ? "update" : "creation";

    try {
      const data: ICompanyInsurance = { ...values, companyId: Company.id, file: values!.file!.attachment };

      action === "update"
        ? await putCompanyInsurance(values.id!, data)
        : await createCompanyInsurance(Company.id, data);

      actions.setSubmitting(false);
      editableProps.handleCancelClick();
      await refetch();

      addToast({
        text: t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.TOAST.SUCCESS", { action }),
        type: "success",
      });
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.TOAST.FAILURE", { action }),
        type: "danger",
      });
    }
  };

  return (
    <>
      {editableProps.editMode ? (
        <CompanyInsuranceSectionEdit
          source={Company}
          onSubmit={handleSubmit}
          editableProps={editableProps}
          initialValues={initialValues}
          validationSchema={bankingDetailsSchema}
          onCancel={editableProps.handleCancelClick}
        />
      ) : (
        <CompanyInsurancePreview insurance={fetchedInsurance} />
      )}
    </>
  );
};
